import { API_BASE_URL } from '../config';
import {
  UtmAnalyticsResponse,
  CampaignMetric,
  SourceMetric,
  TimeSeriesPoint,
  DeviceBreakdown,
  LeadConversion,
  GroupedLeadConversion,
  QuoteDetail
} from '../types';

export interface AnalyticsDateRange {
  startDate?: string; // YYYY-MM-DD format
  endDate?: string;   // YYYY-MM-DD format
}

export interface ConversionsFilter extends AnalyticsDateRange {
  utmCampaign?: string;
  utmSource?: string;
}

/**
 * Fetch complete UTM analytics data
 */
export async function fetchAnalytics(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<UtmAnalyticsResponse> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar analytics');
  }

  const payload = await response.json();
  // API wraps response in { success: true, data: {...} }
  return payload.data;
}

/**
 * Fetch campaign performance metrics
 */
export async function fetchCampaignMetrics(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<CampaignMetric[]> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/campaigns${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar métricas de campanhas');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Fetch source/medium breakdown
 */
export async function fetchSourceMetrics(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<SourceMetric[]> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/sources${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar métricas de fontes');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Fetch time series data
 */
export async function fetchTimeSeries(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<TimeSeriesPoint[]> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/timeseries${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar série temporal');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Fetch device breakdown
 */
export async function fetchDeviceBreakdown(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<DeviceBreakdown> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/devices${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar dados de dispositivos');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Fetch lead-to-quote conversions
 */
export async function fetchConversions(
  accessToken: string,
  filter?: ConversionsFilter
): Promise<LeadConversion[]> {
  const params = new URLSearchParams();

  if (filter?.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter?.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter?.utmCampaign) {
    params.append('utmCampaign', filter.utmCampaign);
  }
  if (filter?.utmSource) {
    params.append('utmSource', filter.utmSource);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/conversions${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar conversões');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Export campaign metrics as CSV
 */
export async function exportCampaignsCsv(
  accessToken: string,
  dateRange?: AnalyticsDateRange
): Promise<Blob> {
  const params = new URLSearchParams();

  if (dateRange?.startDate) {
    params.append('startDate', dateRange.startDate);
  }
  if (dateRange?.endDate) {
    params.append('endDate', dateRange.endDate);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/export/campaigns${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao exportar campanhas');
  }

  return await response.blob();
}

/**
 * Export conversions as CSV
 */
export async function exportConversionsCsv(
  accessToken: string,
  filter?: ConversionsFilter
): Promise<Blob> {
  const params = new URLSearchParams();

  if (filter?.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter?.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter?.utmCampaign) {
    params.append('utmCampaign', filter.utmCampaign);
  }
  if (filter?.utmSource) {
    params.append('utmSource', filter.utmSource);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/export/conversions${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao exportar conversões');
  }

  return await response.blob();
}

/**
 * Helper to download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Fetch grouped lead conversions (deduplicated by email)
 */
export async function fetchGroupedConversions(
  accessToken: string,
  filter?: ConversionsFilter
): Promise<GroupedLeadConversion[]> {
  const params = new URLSearchParams();

  if (filter?.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter?.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter?.utmCampaign) {
    params.append('utmCampaign', filter.utmCampaign);
  }
  if (filter?.utmSource) {
    params.append('utmSource', filter.utmSource);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/conversions/grouped${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar conversões');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Fetch all quotes for a specific email
 */
export async function fetchQuotesByEmail(
  accessToken: string,
  email: string
): Promise<QuoteDetail[]> {
  const params = new URLSearchParams();
  params.append('email', email);

  const endpoint = `${API_BASE_URL}/backoffice/leads/analytics/quotes-by-email?${params.toString()}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar orçamentos');
  }

  const payload = await response.json();
  return payload.data;
}
